import { useQuery } from 'convex/react'
import { api } from '../../../../convex/_generated/api'
import { useAuth } from '@/contexts/auth-context'
import { cn } from '@/lib/utils'
import { useState } from 'react'
import {
  CheckCircle2,
  XCircle,
  Loader2,
  Search,
  Bug,
  Rocket,
  Eye,
  TestTube2,
  Activity,
  FlaskConical,
  Zap,
  Skull,
  Sparkles,
  HeartPulse,
} from 'lucide-react'

const RUN_TYPE_CONFIG: Record<string, { label: string; icon: typeof Activity; color: string; scope: 'issue' | 'experiment' }> = {
  daemon_pass: { label: 'Daemon Pass', icon: Activity, color: 'bg-neutral-100 text-neutral-700', scope: 'issue' },
  verification: { label: 'Verification', icon: Search, color: 'bg-blue-50 text-blue-700', scope: 'issue' },
  auto_fix: { label: 'Auto Fix', icon: Bug, color: 'bg-orange-50 text-orange-700', scope: 'issue' },
  eval_replay: { label: 'Eval Replay', icon: TestTube2, color: 'bg-violet-50 text-violet-700', scope: 'issue' },
  monitoring: { label: 'Monitoring', icon: Eye, color: 'bg-green-50 text-green-700', scope: 'issue' },
  deploy: { label: 'Deploy', icon: Rocket, color: 'bg-red-50 text-red-700', scope: 'issue' },
  optimize_round: { label: 'Optimize', icon: FlaskConical, color: 'bg-indigo-50 text-indigo-700', scope: 'experiment' },
  experiment_monitor: { label: 'Monitor', icon: Eye, color: 'bg-cyan-50 text-cyan-700', scope: 'experiment' },
  experiment_ship: { label: 'Ship', icon: Zap, color: 'bg-emerald-50 text-emerald-700', scope: 'experiment' },
  experiment_kill: { label: 'Kill', icon: Skull, color: 'bg-rose-50 text-rose-700', scope: 'experiment' },
  experiment_polish: { label: 'Polish', icon: Sparkles, color: 'bg-amber-50 text-amber-700', scope: 'experiment' },
  experiment_health_check: { label: 'Health', icon: HeartPulse, color: 'bg-teal-50 text-teal-700', scope: 'experiment' },
}

const STATUS_STYLE: Record<string, { icon: typeof CheckCircle2; color: string }> = {
  completed: { icon: CheckCircle2, color: 'text-green-600' },
  failed: { icon: XCircle, color: 'text-red-500' },
  running: { icon: Loader2, color: 'text-blue-500 animate-spin' },
}

type Scope = 'all' | 'issue' | 'experiment'

function formatDuration(ms: number | undefined): string {
  if (!ms) return '—'
  if (ms < 1000) return `${ms}ms`
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`
  return `${(ms / 60000).toFixed(1)}m`
}

function formatAge(ts: number): string {
  const diff = Date.now() - ts
  if (diff < 60000) return 'just now'
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`
  return `${Math.floor(diff / 86400000)}d ago`
}

function StatCard({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div className="bg-white border border-neutral-200 rounded-lg px-4 py-3">
      <div className="text-[11px] font-medium text-neutral-500 uppercase tracking-wider">{label}</div>
      <div className="text-2xl font-semibold text-neutral-900 mt-0.5">{value}</div>
      {sub && <div className="text-[11px] text-neutral-400 mt-0.5">{sub}</div>}
    </div>
  )
}

export default function AdminPipelinePage() {
  const { user, isAuthenticated } = useAuth()
  const isAdmin = isAuthenticated && user?.isAdmin
  const stats = useQuery(api.admin.getPipelineStats)
  const runs = useQuery(api.admin.listPipelineRunsAdmin, { limit: 100 })
  const [scope, setScope] = useState<Scope>('all')
  const [filter, setFilter] = useState<string | null>(null)

  if (!isAdmin) {
    return <div className="p-6 text-sm text-neutral-500">Admin access required</div>
  }

  const scopeTypes = Object.entries(RUN_TYPE_CONFIG)
    .filter(([, v]) => scope === 'all' || v.scope === scope)
    .map(([k]) => k)

  const filteredRuns = runs?.filter((r: any) => {
    if (filter) return r.runType === filter
    return scope === 'all' || scopeTypes.includes(r.runType)
  })

  const issueTypes = Object.entries(RUN_TYPE_CONFIG).filter(([, v]) => v.scope === 'issue').map(([k]) => k)
  const experimentTypes = Object.entries(RUN_TYPE_CONFIG).filter(([, v]) => v.scope === 'experiment').map(([k]) => k)

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-neutral-900">Pipeline</h1>
          <p className="text-sm text-neutral-500 mt-0.5">
            Issue fixes + experiment lifecycle
          </p>
        </div>
        <div className="flex items-center gap-1 bg-neutral-100 rounded-lg p-0.5">
          {(['all', 'issue', 'experiment'] as Scope[]).map((s) => (
            <button
              key={s}
              onClick={() => { setScope(s); setFilter(null) }}
              className={cn(
                'px-3 py-1 rounded-md text-xs font-medium transition-colors',
                scope === s ? 'bg-white text-neutral-900 shadow-sm' : 'text-neutral-500 hover:text-neutral-700',
              )}
            >
              {s === 'all' ? 'All' : s === 'issue' ? 'Issues' : 'Experiments'}
            </button>
          ))}
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard
            label="Last 24h"
            value={stats.last24h.count}
            sub={scope === 'experiment'
              ? `${(stats as any).experiments?.last24h ?? 0} experiment runs`
              : scope === 'issue'
                ? `${(stats as any).issues?.last24h ?? 0} issue runs`
                : undefined}
          />
          <StatCard
            label="Last 7d"
            value={stats.last7d.count}
            sub={scope === 'experiment'
              ? `${(stats as any).experiments?.last7d ?? 0} experiment runs`
              : scope === 'issue'
                ? `${(stats as any).issues?.last7d ?? 0} issue runs`
                : undefined}
          />
          <StatCard
            label="Success Rate (24h)"
            value={stats.last24h.count > 0
              ? `${Math.round(Object.values(stats.last24h.byType as Record<string, any>).reduce((a, b) => a + b.completed, 0) / stats.last24h.count * 100)}%`
              : '—'}
          />
          <StatCard
            label="Last Run"
            value={stats.lastRun ? formatAge(stats.lastRun.startedAt) : '—'}
            sub={stats.lastRun ? `${RUN_TYPE_CONFIG[stats.lastRun.runType]?.label ?? stats.lastRun.runType} · ${stats.lastRun.status}` : undefined}
          />
        </div>
      )}

      {/* Pipeline stages overview */}
      {stats?.last24h.byType && (
        <div className="bg-white border border-neutral-200 rounded-lg p-4">
          <h2 className="text-sm font-medium text-neutral-700 mb-3">
            {scope === 'experiment' ? 'Experiment Stages' : scope === 'issue' ? 'Issue Stages' : 'Pipeline Stages'} (24h)
          </h2>
          <div className="flex items-center gap-1 flex-wrap">
            {(scope === 'experiment' ? experimentTypes : scope === 'issue' ? issueTypes : [...issueTypes, '|', ...experimentTypes]).map((type, i) => {
              if (type === '|') {
                return <div key="sep" className="w-px h-6 bg-neutral-200 mx-2" />
              }
              const data = (stats.last24h.byType as Record<string, any>)[type]
              const cfg = RUN_TYPE_CONFIG[type]
              if (!cfg) return null
              const Icon = cfg.icon
              return (
                <div key={type} className="flex items-center">
                  {i > 0 && type !== '|' && <div className="w-4 h-px bg-neutral-200 mx-0.5" />}
                  <button
                    onClick={() => setFilter(filter === type ? null : type)}
                    className={cn(
                      'flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border transition-colors',
                      filter === type
                        ? 'bg-neutral-900 text-white border-neutral-900'
                        : data
                          ? cfg.color + ' border-current/20 hover:opacity-80'
                          : 'bg-neutral-50 text-neutral-400 border-neutral-100 hover:bg-neutral-100',
                    )}
                  >
                    <Icon className="h-3 w-3" />
                    <span>{cfg.label}</span>
                    {data && (
                      <span className="ml-0.5 font-mono text-[10px]">
                        {data.completed}/{data.total}
                        {data.failed > 0 && <span className="text-red-500"> ({data.failed}x)</span>}
                      </span>
                    )}
                  </button>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Run list */}
      <div className="rounded-lg border border-neutral-200 bg-white divide-y divide-neutral-100">
        {filteredRuns === undefined ? (
          <div className="px-4 py-3 text-sm text-neutral-400">Loading...</div>
        ) : filteredRuns.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-neutral-400">No pipeline runs yet.</div>
        ) : (
          filteredRuns.map((run: any) => {
            const typeCfg = RUN_TYPE_CONFIG[run.runType] ?? RUN_TYPE_CONFIG.daemon_pass
            const statusCfg = STATUS_STYLE[run.status] ?? STATUS_STYLE.running
            const StatusIcon = statusCfg.icon
            const TypeIcon = typeCfg.icon
            return (
              <div key={run._id} className="px-4 py-2.5 flex items-center gap-3 hover:bg-neutral-50">
                <StatusIcon className={cn('h-4 w-4 flex-shrink-0', statusCfg.color)} />
                <div className={cn(
                  'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider border flex-shrink-0',
                  typeCfg.color, 'border-current/20',
                )}>
                  <TypeIcon className="h-3 w-3" />
                  {typeCfg.label}
                </div>
                {run.issueNumber != null && (
                  <a
                    href={`/admin/issues/${run.issueNumber}`}
                    className="text-xs font-mono text-blue-600 hover:underline flex-shrink-0"
                  >
                    ISS-{run.issueNumber}
                  </a>
                )}
                {run.flagKey && (
                  <a
                    href={`/admin/flags?q=${run.flagKey}`}
                    className="text-xs font-mono text-indigo-600 hover:underline flex-shrink-0"
                  >
                    {run.flagKey}
                  </a>
                )}
                <div className="flex-1 min-w-0 text-xs text-neutral-600 truncate">
                  {run.summary ?? '—'}
                </div>
                <div className="text-[11px] text-neutral-400 flex-shrink-0 tabular-nums">
                  {formatDuration(run.durationMs)}
                </div>
                <div className="text-[11px] text-neutral-400 flex-shrink-0 w-16 text-right">
                  {formatAge(run.startedAt)}
                </div>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}
